import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

const STATUS_LABELS = {
  0: 'Ativo',
  1: 'Em Avaliação',
  2: 'Cadastrado',
  3: 'Ex Paciente',
  4: 'Pausado',
};

const TURNO_LABELS = { 0: 'Manhã', 1: 'Tarde' };

const calcularIdade = (birthdateStr) => {
  if (!birthdateStr) return '—';
  const [year, month, day] = String(birthdateStr).split('-').map(Number);
  if (!year || !month || !day) return '—';
  const birthDate = new Date(year, month - 1, day);
  if (Number.isNaN(birthDate.getTime())) return '—';
  const today = new Date();
  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  if (today.getDate() < birthDate.getDate()) months -= 1;
  if (months < 0) { years -= 1; months += 12; }
  const anosLabel = years === 1 ? ' ano' : ' anos';
  const mesesLabel = months === 1 ? ' mês' : ' meses';
  return `${years}${anosLabel}${months > 0 ? ` e ${months}${mesesLabel}` : ''}`.trim();
};

const PacienteListRow = ({ paciente, defaultImageUrl, onLoadImage, modalityName: modalityNameProp }) => {
  const [imgUrl, setImgUrl] = useState(paciente.imageUrl || null);
  const [imgLoading, setImgLoading] = useState(!paciente.imageUrl);
  const imgRef = useRef();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '50px', threshold: 0.01 }
    );
    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || imgUrl || !imgLoading) return;
    let cancelled = false;
    onLoadImage(paciente.id).then((url) => {
      if (!cancelled) {
        setImgUrl(url);
        setImgLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [isVisible, paciente.id, onLoadImage, imgLoading, imgUrl]);

  const interventionDate = paciente.interventionStartDate
    ? new Date(paciente.interventionStartDate)
    : null;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  let badgeBorda = null;
  if (interventionDate) {
    const dataLimite = new Date(interventionDate);
    dataLimite.setDate(dataLimite.getDate() + 30);
    dataLimite.setHours(0, 0, 0, 0);
    if (dataLimite >= hoje) badgeBorda = 'recente';
  }
  if (!badgeBorda && paciente.statusId === 1) badgeBorda = 'avaliacao';

  const statusLabel = STATUS_LABELS[paciente.statusId] ?? '—';
  const modalityName = modalityNameProp ?? paciente.modalityName ?? paciente.modality?.name ?? '—';
  const idadeStr = calcularIdade(paciente.birthdate);
  const turnoLabel = paciente.shift != null ? (TURNO_LABELS[Number(paciente.shift)] ?? '—') : '—';

  const rowClass = [
    'paciente-list-row-wrapper',
    badgeBorda === 'recente' && 'paciente-list-row-wrapper--recente',
    badgeBorda === 'avaliacao' && 'paciente-list-row-wrapper--avaliacao',
  ].filter(Boolean).join(' ');

  return (
    <div ref={imgRef} className={rowClass}>
      <Link
        to={`/Pacientes/PerfilPaciente/${paciente.id}`}
        state={{ fromBeta: true, returnScrollToId: paciente.id }}
        className="paciente-list-row"
      >
        <div className="paciente-list-row__avatar-wrap">
          {imgLoading && (
            <span className="paciente-list-row__avatar-spinner" aria-hidden="true" />
          )}
          <img
            src={isVisible ? (imgUrl || defaultImageUrl) : defaultImageUrl}
            alt=""
            className="paciente-list-row__avatar"
            style={{ opacity: imgLoading ? 0.4 : 1 }}
          />
        </div>
        <div className="paciente-list-row__info">
          <span className="paciente-list-row__name">{paciente.name}</span>
        </div>
        <span className="paciente-list-row__idade">{idadeStr}</span>
        <span className="paciente-list-row__turno">{turnoLabel}</span>
        <span className="paciente-list-row__modality">{modalityName}</span>
        <span className="paciente-list-row__status">{statusLabel}</span>
        <span className="paciente-list-row__chevron">
          <i className="fa-solid fa-chevron-right" />
        </span>
      </Link>
    </div>
  );
};

export default PacienteListRow;
